import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Users, Plus, Search, Trash2, Pencil, ChevronLeft, ChevronRight,
  FolderOpen, X, UserPlus, MoreHorizontal, Filter, Eye, Shield, ArrowUpDown,
  Activity,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import GroupManager from "@/components/contacts/GroupManager";
import ContactForm from "@/components/contacts/ContactForm";
import ImportExportPanel from "@/components/contacts/ImportExportPanel";
import ActivityFeed from "@/components/contacts/ActivityFeed";

export default function ContactsPage() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [tags, setTags] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState(new Set());

  // Dialogs
  const [showForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState(null);
  const [showGroups, setShowGroups] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showVisibility, setShowVisibility] = useState(false);
  const [showImportExport, setShowImportExport] = useState(false);
  const [showActivity, setShowActivity] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set("page", page);
      params.set("limit", "50");
      if (search) params.set("search", search);
      if (activeTag) params.set("tag", activeTag);
      if (activeGroup) params.set("group_id", activeGroup);

      const { data } = await api.get(`/contacts?${params}`);
      setContacts(data.contacts);
      setTotal(data.total);
      setTotalPages(data.pages);
    } catch {
      toast.error("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }, [page, search, activeTag, activeGroup]);

  const fetchGroups = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/groups");
      setGroups(data.groups);
    } catch {}
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/tags");
      setTags(data.tags);
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.get("/contacts/stats/overview");
      setStats(data);
    } catch {}
  }, []);

  const fetchSenderAccounts = useCallback(async () => {
    try {
      const { data } = await api.get("/aliases");
      const aliases = Array.isArray(data) ? data : data.aliases || [];
      setSenderAccounts(aliases);
    } catch {}
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    fetchGroups();
    fetchTags();
    fetchStats();
    fetchSenderAccounts();
  }, [fetchGroups, fetchTags, fetchStats, fetchSenderAccounts]);

  const handleDelete = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success("Contact deleted");
      fetchContacts();
      fetchStats();
      fetchTags();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleBatchDelete = async () => {
    if (selected.size === 0) return;
    try {
      await api.post("/contacts/batch-delete", { contact_ids: Array.from(selected) });
      toast.success(`${selected.size} contact(s) deleted`);
      setSelected(new Set());
      fetchContacts();
      fetchStats();
      fetchTags();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleBatchVisibility = async (visibility) => {
    if (selected.size === 0) return;
    try {
      await api.put("/contacts/visibility", {
        contact_ids: Array.from(selected),
        visibility,
        sender_ids: [],
      });
      toast.success(`Visibility updated for ${selected.size} contact(s)`);
      setSelected(new Set());
      setShowVisibility(false);
      fetchContacts();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditContact(null);
    fetchContacts();
    fetchGroups();
    fetchTags();
    fetchStats();
  };

  const handleGroupsChange = () => {
    fetchGroups();
    fetchContacts();
    fetchStats();
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === contacts.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(contacts.map((c) => c.contact_id)));
    }
  };

  const getGroupName = (gid) => groups.find((g) => g.group_id === gid)?.name || gid;

  const clearFilters = () => {
    setActiveTag(null);
    setActiveGroup(null);
    setSearch("");
    setPage(1);
  };

  const hasFilters = activeTag || activeGroup || search;

  return (
    <div className="h-full flex flex-col" data-testid="contacts-page">
      {/* Header */}
      <div className="shrink-0 border-b border-[#E5E5E5] bg-white">
        <div className="px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-medium text-[#111111] tracking-tight" data-testid="contacts-title">
              Contacts
            </h1>
            <p className="text-sm text-zinc-500 mt-0.5">
              {stats ? `${stats.total_contacts} contacts, ${stats.total_groups} groups` : "Loading..."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={showActivity ? "secondary" : "outline"}
              size="sm"
              onClick={() => setShowActivity(!showActivity)}
              data-testid="activity-feed-btn"
            >
              <Activity size={15} className="mr-1.5" /> Activity
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImportExport(true)}
              data-testid="import-export-btn"
            >
              <ArrowUpDown size={15} className="mr-1.5" /> Import / Export
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowGroups(true)}
              data-testid="manage-groups-btn"
            >
              <FolderOpen size={15} className="mr-1.5" /> Groups
            </Button>
            <Button
              size="sm"
              onClick={() => { setEditContact(null); setShowForm(true); }}
              data-testid="add-contact-btn"
            >
              <UserPlus size={15} className="mr-1.5" /> Add contact
            </Button>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="px-6 pb-3 flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="pl-9 h-9 text-sm"
              data-testid="contacts-search"
            />
          </div>

          <Button
            variant={showFilter ? "secondary" : "outline"}
            size="sm"
            onClick={() => setShowFilter(!showFilter)}
            data-testid="filter-toggle-btn"
          >
            <Filter size={14} className="mr-1.5" />
            Filter
            {hasFilters && <Badge className="ml-1.5 bg-blue-600 text-white text-[9px] px-1.5">ON</Badge>}
          </Button>

          {selected.size > 0 && (
            <>
              <DropdownMenu open={showVisibility} onOpenChange={setShowVisibility}>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="batch-visibility-btn">
                    <Eye size={14} className="mr-1.5" /> Visibility ({selected.size})
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => handleBatchVisibility("only_me")} data-testid="set-visibility-only-me">
                    <Shield size={13} className="mr-2" /> Only me (private)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleBatchVisibility("all_senders")} data-testid="set-visibility-all-senders">
                    <Users size={13} className="mr-2" /> All sender accounts
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem disabled className="text-xs text-zinc-500">
                    For specific senders, edit individual contacts
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button variant="destructive" size="sm" onClick={handleBatchDelete} data-testid="batch-delete-btn">
                <Trash2 size={14} className="mr-1.5" /> Delete ({selected.size})
              </Button>
            </>
          )}
        </div>

        {/* Filter bar */}
        {showFilter && (
          <div className="px-6 pb-3 flex items-center gap-3 flex-wrap" data-testid="filter-bar">
            <Select
              value={activeGroup || "all"}
              onValueChange={(v) => { setActiveGroup(v === "all" ? null : v); setPage(1); }}
            >
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-group-select">
                <SelectValue placeholder="All groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All groups</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.group_id} value={g.group_id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={activeTag || "all"}
              onValueChange={(v) => { setActiveTag(v === "all" ? null : v); setPage(1); }}
            >
              <SelectTrigger className="h-8 w-44 text-xs" data-testid="filter-tag-select">
                <SelectValue placeholder="All tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tags</SelectItem>
                {tags.map((t) => (
                  <SelectItem key={t.tag} value={t.tag}>{t.tag} ({t.count})</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-xs text-zinc-500">
                <X size={12} className="mr-1" /> Clear filters
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Main content area — split when activity panel is open */}
      <div className="flex-1 flex overflow-hidden">
        {/* Contact list */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-60 text-center" data-testid="contacts-empty">
            <Users size={40} className="text-zinc-300 mb-3" />
            <p className="text-sm font-medium text-zinc-500">
              {hasFilters ? "No contacts match your filters" : "No contacts yet"}
            </p>
            <p className="text-xs text-zinc-500 mt-1">
              {hasFilters ? "Try adjusting your search or filters" : "Add your first contact to get started"}
            </p>
            {!hasFilters && (
              <Button size="sm" className="mt-4" onClick={() => setShowForm(true)} data-testid="empty-add-contact-btn">
                <Plus size={14} className="mr-1.5" /> Add contact
              </Button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-[#2A2A2A]">
            {/* Select all header */}
            <div className="px-6 py-2 flex items-center gap-3 bg-[#F5F5F5]/50 text-xs text-zinc-500">
              <Checkbox
                checked={selected.size === contacts.length && contacts.length > 0}
                onCheckedChange={toggleSelectAll}
                data-testid="select-all-checkbox"
              />
              <span className="flex-1">
                {total} contact{total !== 1 ? "s" : ""}
                {hasFilters && " (filtered)"}
              </span>
            </div>

            {contacts.map((c) => (
              <div
                key={c.contact_id}
                className="px-6 py-3 flex items-center gap-3 hover:bg-[#F5F5F5] transition-colors group"
                data-testid={`contact-row-${c.contact_id}`}
              >
                <Checkbox
                  checked={selected.has(c.contact_id)}
                  onCheckedChange={() => toggleSelect(c.contact_id)}
                  data-testid={`contact-checkbox-${c.contact_id}`}
                />

                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-medium text-[#888888] shrink-0">
                  {(c.name || c.email).charAt(0).toUpperCase()}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-[#111111] truncate">
                      {c.name || c.email}
                    </p>
                  </div>
                  {c.name && (
                    <p className="text-xs text-zinc-500 font-mono truncate">{c.email}</p>
                  )}
                </div>

                {/* Tags */}
                <div className="hidden md:flex items-center gap-1.5 shrink-0">
                  {c.tags?.slice(0, 3).map((t) => (
                    <Badge
                      key={t}
                      variant="secondary"
                      className="text-[10px] px-1.5 py-0 font-normal cursor-pointer hover:bg-[#EBEBEB]"
                      onClick={() => { setActiveTag(t); setShowFilter(true); setPage(1); }}
                    >
                      {t}
                    </Badge>
                  ))}
                  {c.tags?.length > 3 && (
                    <span className="text-[10px] text-zinc-500">+{c.tags.length - 3}</span>
                  )}
                </div>

                {/* Groups */}
                <div className="hidden lg:flex items-center gap-1.5 shrink-0">
                  {c.group_ids?.slice(0, 2).map((gid) => (
                    <Badge
                      key={gid}
                      variant="outline"
                      className="text-[10px] px-1.5 py-0 font-normal cursor-pointer hover:bg-[#F5F5F5]"
                      onClick={() => { setActiveGroup(gid); setShowFilter(true); setPage(1); }}
                    >
                      <FolderOpen size={9} className="mr-0.5" />
                      {getGroupName(gid)}
                    </Badge>
                  ))}
                  {c.group_ids?.length > 2 && (
                    <span className="text-[10px] text-zinc-500">+{c.group_ids.length - 2}</span>
                  )}
                </div>

                {/* Visibility indicator */}
                <div className="hidden md:flex items-center shrink-0" title={
                  c.visibility === "all_senders" ? "Visible to all senders" :
                  c.visibility === "specific_senders" ? "Visible to specific senders" :
                  "Private"
                }>
                  {c.visibility === "all_senders" && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 text-green-600 border-green-200">
                      <Users size={8} className="mr-0.5" /> All
                    </Badge>
                  )}
                  {c.visibility === "specific_senders" && (
                    <Badge variant="outline" className="text-[9px] px-1 py-0 text-blue-600 border-blue-200">
                      <Eye size={8} className="mr-0.5" /> Specific
                    </Badge>
                  )}
                  {c.visibility === "only_me" && (
                    <Shield size={12} className="text-zinc-300" />
                  )}
                </div>

                {/* Actions */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`contact-actions-${c.contact_id}`}
                    >
                      <MoreHorizontal size={15} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => { setEditContact(c); setShowForm(true); }}
                      data-testid={`contact-edit-${c.contact_id}`}
                    >
                      <Pencil size={14} className="mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600 focus:text-red-600"
                      onClick={() => handleDelete(c.contact_id)}
                      data-testid={`contact-delete-${c.contact_id}`}
                    >
                      <Trash2 size={14} className="mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="shrink-0 border-t border-[#E5E5E5] px-6 py-3 flex items-center justify-between bg-white" data-testid="contacts-pagination">
          <p className="text-xs text-zinc-500">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-1.5">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              data-testid="pagination-prev"
            >
              <ChevronLeft size={14} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              data-testid="pagination-next"
            >
              <ChevronRight size={14} />
            </Button>
          </div>
        </div>
      )}
        </div>

        {/* Activity Feed Panel */}
        {showActivity && (
          <div className="w-80 shrink-0 border-l border-[#E5E5E5] bg-white flex flex-col" data-testid="activity-panel">
            <div className="px-4 py-3.5 border-b border-[#E5E5E5] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity size={15} className="text-zinc-500" />
                <span className="text-sm font-medium text-[#555555]">Activity</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={() => setShowActivity(false)}
                data-testid="activity-close-btn"
              >
                <X size={14} />
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <ActivityFeed />
            </div>
          </div>
        )}
      </div>

      {/* Contact Form Dialog */}
      <ContactForm
        open={showForm}
        onOpenChange={(v) => { setShowForm(v); if (!v) setEditContact(null); }}
        contact={editContact}
        groups={groups}
        senderAccounts={senderAccounts}
        onSuccess={handleFormSuccess}
      />

      {/* Group Manager Dialog */}
      <GroupManager
        open={showGroups}
        onOpenChange={setShowGroups}
        groups={groups}
        onChange={handleGroupsChange}
      />

      {/* Import/Export Dialog */}
      <ImportExportPanel
        open={showImportExport}
        onOpenChange={setShowImportExport}
        groups={groups}
        onImportDone={handleFormSuccess}
      />
    </div>
  );
}
